import {
  Box,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { latestLevels, summariseLevels } from '@karehero/helpers/audio';
import { formatDuration } from '@karehero/helpers/datetime';

import { Icon } from '@/components/atoms/Icon/Icon';
import { radius } from '@/theme/radius';

import { ATTACHMENT_BUBBLE_MAX_WIDTH } from './attachmentBubble';

export interface VoiceNoteBubbleProps {
  durationSeconds: number;
  /** Sampled sound levels, 0–1. Omit to derive a shape from the duration. */
  levels?: readonly number[];
  /** A sent note, an unsent draft, one still being made, or one that failed. */
  tone?: 'filled' | 'outlined' | 'recording' | 'failed';
  isPlaying?: boolean;
  /** Omit when the note can't be played back. */
  onTogglePlay?: () => void;
  /** What was heard as it was recorded, to read under the audio. */
  transcript?: string;
  /** Whether the words show; one setting for the whole chat, so held above. */
  isTranscriptShown?: boolean;
  /** Shows or hides the words; omit to offer no words. */
  onToggleTranscript?: () => void;
}

/** A sent note's width, where the thread has room for it. */
const SENT_NOTE_WIDTH = 220;

const BAR_COUNT = 28;
const BAR_MIN_HEIGHT = 5;
const BAR_RANGE = 13;

const BUBBLE_VARIANT = {
  filled: 'chatBubbleFilled',
  failed: 'chatBubbleFailed',
  outlined: 'chatBubbleOutlined',
  recording: 'chatBubbleOutlined',
} as const;

const barHeight = (level: number) =>
  BAR_MIN_HEIGHT + Math.round(level * BAR_RANGE);

// Fallback without sampled levels: a still shape derived from the duration.
const derivedHeights = (seconds: number): number[] =>
  Array.from({ length: BAR_COUNT }, (_, i) =>
    barHeight(Math.abs(Math.sin(i * 1.7 + seconds) * Math.cos(i * 0.6))),
  );

// A recording scrolls its latest levels; a finished note shows the whole of it.
const sampledHeights = (
  levels: readonly number[],
  isRecording: boolean,
): number[] =>
  (isRecording
    ? latestLevels(levels, BAR_COUNT)
    : summariseLevels(levels, BAR_COUNT)
  ).map(barHeight);

/** VoiceNoteBubble renders a voice note as a chat bubble. */
export const VoiceNoteBubble = ({
  durationSeconds,
  levels,
  tone = 'filled',
  isPlaying = false,
  onTogglePlay,
  transcript,
  isTranscriptShown = false,
  onToggleTranscript,
}: VoiceNoteBubbleProps) => {
  const isRecording = tone === 'recording';
  const heights = levels?.length
    ? sampledHeights(levels, isRecording)
    : derivedHeights(durationSeconds);

  const isFilled = tone === 'filled' || tone === 'failed';
  const isReadable = !!transcript && !!onToggleTranscript && isFilled;

  return (
    <Paper
      variant={BUBBLE_VARIANT[tone]}
      sx={
        isFilled
          ? { maxWidth: ATTACHMENT_BUBBLE_MAX_WIDTH }
          : { borderRadius: `${radius.lg}px` }
      }
    >
      <Stack
        direction='row'
        spacing={1}
        // In the thread a note keeps its size where it fits; in the composer it gives way to the buttons.
        sx={{
          alignItems: 'center',
          ...(isFilled
            ? { width: SENT_NOTE_WIDTH, maxWidth: '100%' }
            : { minWidth: 0 }),
        }}
      >
        {isRecording ? (
          <Box sx={{ display: 'flex', px: 0.5, color: 'custom.coral.600' }}>
            <Icon icon='microphone' fontSize={14} />
          </Box>
        ) : (
          <IconButton
            aria-label={isPlaying ? 'Pause' : 'Play'}
            size='small'
            color='inherit'
            disabled={!onTogglePlay}
            onClick={onTogglePlay}
          >
            <Icon icon={isPlaying ? 'pause' : 'play'} fontSize={14} />
          </IconButton>
        )}

        <Stack
          direction='row'
          spacing='2px'
          aria-hidden
          sx={{
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            height: 24,
            overflow: 'hidden',
          }}
        >
          {heights.map((height, i) => (
            <Box
              key={i}
              sx={{
                width: 2,
                height,
                borderRadius: '1px',
                bgcolor: 'currentColor',
                opacity: isFilled ? 0.8 : 0.4,
              }}
            />
          ))}
        </Stack>

        <Typography variant='caption' sx={{ pr: 0.5, color: 'inherit' }}>
          {formatDuration(durationSeconds)}
        </Typography>
      </Stack>
      {isReadable && (
        // In the same bubble, as the words belong to this note. Read only:
        // anything wrong is corrected in the summary.
        <Box sx={{ px: 1 }}>
          <Collapse in={isTranscriptShown}>
            <Typography variant='body2' sx={{ pt: 2, color: 'inherit' }}>
              {transcript}
            </Typography>
          </Collapse>
          <Link
            component='button'
            variant='caption'
            color='inherit'
            aria-expanded={isTranscriptShown}
            onClick={onToggleTranscript}
            sx={{ py: 1, display: 'block', ml: 'auto' }}
          >
            {isTranscriptShown ? 'Hide text' : 'Show text'}
          </Link>
        </Box>
      )}
    </Paper>
  );
};

VoiceNoteBubble.displayName = 'VoiceNoteBubble';
export default VoiceNoteBubble;
